import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { useEffect, useState } from 'react'
import {
    MembershipApiItem,
    MembershipItem,
    denormalizeMembershipItem,
} from 'types/membership'
import { EditIcon } from 'utils/custom-icons'
import { classNames } from 'utils/dom-helpers'
import { useFetch } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import { toCurrency } from 'utils/string-localization-helper'
import { FetchMethods } from 'utils/types'

function ServiceModalMemberships() {
    const { translations } = useAppContext()
    const { MembershipModal, ServiceModal } = useAuth()
    const { attributes } = ServiceModal
    const { tenant } = useTenantContext()
    const [memberships, setMemberships] = useState<MembershipItem[]>([])
    const {
        data: memberships_api_response,
        doFetch: getMemberships,
        is_loading,
    } = useFetch(
        `/v1/memberships/by-tenant-id?tenantId=${tenant?.id}`,
        FetchMethods.GET,
        !!tenant?.id
    )

    useEffect(() => {
        if (!is_loading && memberships_api_response) {
            const list: MembershipItem[] = memberships_api_response
                .filter((m: MembershipApiItem) => {
                    return (
                        m.categoryIds?.some(
                            (cat_id) => cat_id == attributes?.category
                        ) ||
                        m.serviceIds?.some(
                            (sev_id) => sev_id == attributes?.id
                        )
                    )
                })
                .map(denormalizeMembershipItem)

            if (list) {
                if (list.length > 0) {
                    setMemberships(list)
                } else if (list.length == 0) {
                    setMemberships([])
                }
            }

            if (MembershipModal.attributes?.refetch) {
                MembershipModal.setAttributes({
                    refetch: false,
                })
                getMemberships()
            }
        }
    }, [
        getMemberships,
        memberships_api_response,
        is_loading,
        MembershipModal.attributes?.refetch,
    ])

    function RenderEmptyMembership() {
        return (
            <div
                className={classNames(
                    'justify-center',
                    'overflow-auto max-h-96 h-96 flex-1 flex flex-col p-3'
                )}
            >
                <>
                    <div className="rounded-full bg-gray-200 w-20 h-20 inline-block leading-loose text-center self-center align-middle">
                        <i className="text-gray-400 feather feather-briefcase text-4xl leading-loose" />
                    </div>
                    <Translation
                        render_as="div"
                        className= "text-xl block mt-4 mx-auto"
                        content_key="blank_memberships_title"
                        translations={translations}
                    />
                    <Translation
                        render_as="div"
                        className="block mx-auto mb-3"
                        content_key="blank_memberships_body"
                        translations={translations}
                    />
                </>
            </div>
        )
    }

    function RenderMembership() {
        return (
            <div className="grid grid-cols-2 gap-5">
                {memberships.map((m) => {
                    return (
                        <div
                            key={m.id}
                            className="grid bg-gray-100 bg-opacity-50 rounded-xl px-2 py-1 text-left"
                        >
                            <span className="text-lg text-black">
                                {m.name}
                            </span>
                            <span className="text-gray-500">
                                {m.category_ids?.length} Categories
                            </span>
                            <span className="text-gray-500 my-2">
                                {toCurrency(m.price as number)}
                            </span>
                            <button
                                className="text-left"
                                type="button"
                                onClick={() => {
                                    MembershipModal.setAttributes({
                                        ...MembershipModal.attributes,
                                        is_edit: true,
                                        edit_membership: m,
                                    })
                                    MembershipModal.open()
                                }}
                                title={getTranslation(
                                    'Edit Membership',
                                    translations
                                )}
                            >
                                <EditIcon/>
                            </button>
                        </div>
                    )
                })}
            </div>
        )
    }

    return (
        <div
            className="relative flex flex-col w-full"
        >
            <div className="text-primary text-xl font-medium">
                <Translation
                    content_key="eligible_memberships_title"
                    translations={translations}
                />
                <Translation
                    render_as="div"
                    className="text-base text-gray-900 mt-2"
                    content_key="eligible_memberships_body"
                    translations={translations}
                />
            </div>

            <div className="text-center my-6 rounded-xl relative flex-1">
                {memberships.length ? (
                    <RenderMembership />
                ) : (
                    <RenderEmptyMembership />
                )}
            </div>
        </div>
    )
}

export default ServiceModalMemberships
